import { memo, useCallback, useMemo } from "react";
import PropTypes from "prop-types";

import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import { ArrowDropDown, ArrowDropUp } from "@mui/icons-material";

import { PaginationAction, Loader } from "components";
import FGTrackingTableRow from "./FGTrackingTableRow";

import { ORDER_TABLE_COLUMNS } from "../constants";

const EMPTY_SELECTED_LINE_ITEMS = [];

const FGTrackingTable = ({
  orders,
  pagination,
  onPageChange,
  onRowsPerPageChange,
  expandedRows,
  onToggleExpand,
  selectedLineItemsByOrder,
  onToggleLineItem,
  onToggleSelectAll,
  sortConfig,
  onSortByToggle,
  onSortDirToggle,
  isLoading,
}) => {
  const theme = useTheme();
  const expandedRowIds = useMemo(() => new Set(expandedRows), [expandedRows]);

  const renderSortHeader = useCallback(
    (label, field) => {
      const isFgReadyBy = field === "fgReadyBy";
      const handler = isFgReadyBy ? onSortByToggle : onSortDirToggle;
      const isActive = sortConfig.sortBy === (isFgReadyBy ? "fgDays" : "aging");

      return (
        <Box
          component="button"
          type="button"
          onClick={() => handler(field)}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.25,
            border: 0,
            padding: 0,
            background: "transparent",
            cursor: "pointer",
          }}
        >
          <Typography
            variant="caption"
            sx={{ color: theme.palette.text.primary, fontWeight: 700 }}
          >
            {label}
          </Typography>
          <Box display="flex" flexDirection="column" lineHeight={0.6}>
            <ArrowDropUp
              sx={{
                fontSize: 16,
                color:
                  isActive && sortConfig.sortDir === "ASC"
                    ? theme.palette.text.primary
                    : theme.palette.grey[700],
                marginBottom: "-8px",
              }}
            />
            <ArrowDropDown
              sx={{
                fontSize: 16,
                color:
                  isActive && sortConfig.sortDir === "DESC"
                    ? theme.palette.text.primary
                    : theme.palette.grey[700],
              }}
            />
          </Box>
        </Box>
      );
    },
    [
      onSortByToggle,
      onSortDirToggle,
      sortConfig.sortBy,
      sortConfig.sortDir,
      theme,
    ],
  );
  return (
    <TableContainer
      component={Paper}
      sx={{
        width: "100%",
        maxWidth: "100%",
        overflowX: "auto",
        boxShadow: 0,
        border: "1px solid",
        borderColor: "grey.300",
      }}
    >
      <Table>
        <TableHead>
          <TableRow>
            {ORDER_TABLE_COLUMNS.map((column) => (
              <TableCell key={column}>
                {column === "FG ready by" ? (
                  renderSortHeader(column, "fgReadyBy")
                ) : column === "Ageing" ? (
                  renderSortHeader(column, "ageing")
                ) : (
                  <Typography
                    variant="caption"
                    sx={{ color: theme.palette.text.primary, fontWeight: 700 }}
                  >
                    {column}
                  </Typography>
                )}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {isLoading ? (
            <TableRow>
              <TableCell colSpan={ORDER_TABLE_COLUMNS.length}>
                <Box
                  sx={{
                    width: "100%",
                    minHeight: "30vh",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginBottom: 15,
                  }}
                >
                  <Loader />
                </Box>
              </TableCell>
            </TableRow>
          ) : orders?.length > 0 ? (
            orders.map((order) => (
              <FGTrackingTableRow
                key={order.orderId}
                order={order}
                isExpanded={expandedRowIds.has(order.orderId)}
                onToggleExpand={onToggleExpand}
                selectedLineItemIds={
                  selectedLineItemsByOrder[order.orderId] ||
                  EMPTY_SELECTED_LINE_ITEMS
                }
                onToggleLineItem={onToggleLineItem}
                onToggleSelectAll={onToggleSelectAll}
                columnLength={ORDER_TABLE_COLUMNS.length}
              />
            ))
          ) : (
            <TableRow>
              <TableCell
                colSpan={ORDER_TABLE_COLUMNS.length}
                align="center"
                sx={{ py: 6 }}
              >
                <Typography color="text.secondary">
                  No orders match the selected filters
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TablePagination
              colSpan={ORDER_TABLE_COLUMNS.length}
              count={pagination.totalCount}
              page={pagination.page}
              rowsPerPage={pagination.rowsPerPage}
              rowsPerPageOptions={[10, 20]}
              onPageChange={onPageChange}
              onRowsPerPageChange={onRowsPerPageChange}
              ActionsComponent={PaginationAction}
              sx={{ borderBottom: 0 }}
            />
          </TableRow>
        </TableFooter>
      </Table>
    </TableContainer>
  );
};

FGTrackingTable.propTypes = {
  orders: PropTypes.arrayOf(PropTypes.object).isRequired,
  pagination: PropTypes.shape({
    page: PropTypes.number.isRequired,
    rowsPerPage: PropTypes.number.isRequired,
    totalCount: PropTypes.number.isRequired,
  }).isRequired,
  onPageChange: PropTypes.func.isRequired,
  onRowsPerPageChange: PropTypes.func.isRequired,
  expandedRows: PropTypes.arrayOf(PropTypes.string).isRequired,
  onToggleExpand: PropTypes.func.isRequired,
  selectedLineItemsByOrder: PropTypes.object.isRequired,
  onToggleLineItem: PropTypes.func.isRequired,
  onToggleSelectAll: PropTypes.func.isRequired,
  sortConfig: PropTypes.shape({
    sortBy: PropTypes.string,
    sortDir: PropTypes.string,
  }).isRequired,
  onSortByToggle: PropTypes.func.isRequired,
  onSortDirToggle: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
};

export default memo(FGTrackingTable);
